/**
 * Procedural Web Audio engine. No external audio files — all SFX are
 * generated as short oscillator chirps so the bundle stays tiny.
 * Honours `prefers-reduced-motion` (users who want less motion also
 * typically want less sound; we treat it as a single accessibility signal
 * in addition to the explicit mute toggle).
 *
 * Usage:
 *   audio.play('coin');
 *   audio.setVolume(0.3);
 *   audio.setMuted(true);
 */

import type { VisemeTimeline } from './types.js';

type Sfx =
  | 'coin'
  | 'fire'
  | 'kill'
  | 'baseHit'
  | 'victory'
  | 'defeat'
  | 'click'
  | 'upgrade'
  // Courier Rush (/planet arcade) voices — same procedural envelope discipline.
  | 'pickup'
  | 'dropoff'
  | 'comboUp'
  | 'timerWarn'
  | 'runStart'
  | 'newBest'
  | 'bonk'
  | 'splash'
  // Siege impact voices — scheduled through `hitTone.ts` (throttle + pitch
  // ladder), never called raw: hero hits arrive up to 8-per-frame.
  | 'hit'
  | 'hitKill'
  // Siege proc signatures (ADR-0280 P1c) — rare by construction (1–2% procs,
  // first-N gate charges, 20% escort bands), so they play unthrottled at full
  // character while the tick rain stays behind the hitTone scheduler.
  | 'gateBlock'
  | 'lastStand'
  | 'execute'
  | 'aerialReset'
  | 'escortArrive'
  // Kart Circuit race cues (one-shots; the sustained engine note lives in
  // kartEngine.ts, which cannot be a VOICES entry because it never ends).
  | 'kartGate'
  | 'kartCount'
  | 'kartGo'
  | 'kartTurbo'
  | 'kartNitro'
  | 'kartDouble'
  | 'kartGauge'
  // Car Park's street layer (wanted stars). The siren is two alternating one-shots
  // the HUD repeats while a patrol car is near, not a sustained voice.
  | 'siren'
  | 'sirenDown'
  | 'pedHit'
  | 'carjack'
  | 'ramPolice'
  | 'wantedUp'
  | 'evaded'
  | 'busted'
  // Grey Hour post office (ADR-0264 PR-4)
  | 'corkPop'
  | 'typeKey'
  // Interior / LOOK-room arrive — paper settling, not a cork.
  | 'paperHush'
  // Inner-sea signatures. Short, bounded one-shots; the continuous water/engine
  // bed lives in seaAmbience.ts and only loads while sailing.
  | 'seaCannon'
  | 'seaBarrage'
  | 'seaPulse'
  | 'seaRepair'
  | 'seaOverdrive'
  | 'seaLevel'
  | 'seaRefit'
  | 'seaSector'
  | 'seaVictory';

interface VoiceSpec {
  /** Starting frequency in Hz. */
  freq: number;
  /** Optional ramp target. */
  to?: number;
  duration: number; // seconds
  type?: OscillatorType;
  /** Linear attack/release in seconds; total <= duration. */
  attack?: number;
  release?: number;
  /** Additional detuned voice for harmonics. */
  detune?: number;
  /**
   * Per-voice loudness trim (multiplies the shared 0.35 peak; default 1).
   * Exists so high-rate voices can sit UNDER the reward stingers — an impact
   * tick firing 3/s at the same peak as `victory` fatigues fast, and the kill
   * thwack has to share a moment with `comboUp` without either clipping.
   */
  gain?: number;
}

const VOICES: Record<Sfx, VoiceSpec> = {
  coin:    { freq: 880, to: 1320, duration: 0.08, type: 'triangle' },
  fire:    { freq: 420, to: 180, duration: 0.06, type: 'square', release: 0.05 },
  kill:    { freq: 620, to: 260, duration: 0.14, type: 'sawtooth' },
  baseHit: { freq: 180, to: 90, duration: 0.22, type: 'sawtooth', release: 0.15 },
  victory: { freq: 520, to: 880, duration: 0.45, type: 'triangle', detune: 12 },
  defeat:  { freq: 220, to: 110, duration: 0.55, type: 'sine' },
  click:   { freq: 760, duration: 0.04, type: 'sine' },
  upgrade: { freq: 440, to: 660, duration: 0.18, type: 'triangle', detune: 7 },
  kartGate:  { freq: 1100, to: 1400, duration: 0.06, type: 'square' },
  kartCount: { freq: 660, duration: 0.09, type: 'sine' },
  kartGo:    { freq: 990, to: 1320, duration: 0.18, type: 'sine' },
  kartTurbo: { freq: 220, to: 880, duration: 0.35, type: 'triangle', detune: 9 },
  // N₂O — a low roar that climbs, under the turbo chirp; double and gauge-full are bright.
  kartNitro: { freq: 90, to: 260, duration: 0.6, type: 'sawtooth', detune: 14, gain: 0.85 },
  kartDouble: { freq: 880, to: 1320, duration: 0.2, type: 'square', gain: 0.7 },
  kartGauge: { freq: 1047, to: 1568, duration: 0.24, type: 'triangle', detune: 7 },
  // Street layer. Hi-lo siren at a trimmed gain so it sits under the crime stings.
  siren:     { freq: 960, to: 980, duration: 0.42, type: 'triangle', detune: 5, attack: 0.03, release: 0.06, gain: 0.42 },
  sirenDown: { freq: 770, to: 760, duration: 0.42, type: 'triangle', detune: 5, attack: 0.03, release: 0.06, gain: 0.42 },
  pedHit:    { freq: 170, to: 70, duration: 0.2, type: 'sine', release: 0.08, gain: 0.95 },
  carjack:   { freq: 300, to: 720, duration: 0.22, type: 'square', gain: 0.55 },
  ramPolice: { freq: 140, to: 60, duration: 0.26, type: 'sawtooth', release: 0.1, gain: 0.8 },
  wantedUp:  { freq: 420, to: 840, duration: 0.3, type: 'square', detune: 9, gain: 0.6 },
  evaded:    { freq: 520, to: 1040, duration: 0.38, type: 'triangle', detune: 9 },
  busted:    { freq: 440, to: 110, duration: 0.7, type: 'sawtooth', detune: 11, gain: 0.8 },
  // Courier Rush — envelopes obey attack+release ≤ duration*0.4 (clamped in playUnsafe).
  pickup:    { freq: 660, to: 990, duration: 0.07, type: 'triangle' },
  dropoff:   { freq: 720, to: 1180, duration: 0.16, type: 'triangle', detune: 8 },
  comboUp:   { freq: 520, to: 1040, duration: 0.12, type: 'square' },
  timerWarn: { freq: 340, to: 300, duration: 0.18, type: 'sine' },
  runStart:  { freq: 440, to: 880, duration: 0.2, type: 'triangle' },
  newBest:   { freq: 600, to: 1320, duration: 0.4, type: 'triangle', detune: 12 },
  bonk:      { freq: 280, to: 150, duration: 0.13, type: 'sine' }, // soft cone-clip "oops"
  // Wade into shallows — short sine plop. Attack+release stay inside the 0.4×
  // duration clamp so a 90ms voice cannot throw on Safari.
  splash:    { freq: 380, to: 160, duration: 0.09, type: 'sine', attack: 0.008, release: 0.04, gain: 0.65 },
  // Siege impact. `hit` is a dry 50ms downward tick — short and trimmed to
  // 0.55 gain because it repeats ~3/s and its job is confirmation, not drama.
  // `hitKill` is the heavier body-drop: low sawtooth crunch, clear of both
  // `kill` (620→260, brighter) and `bonk` (sine, player-damage) so the three
  // stay tellable apart mid-fight.
  hit:       { freq: 1240, to: 780, duration: 0.05, type: 'triangle', gain: 0.55 },
  hitKill:   { freq: 420, to: 150, duration: 0.16, type: 'sawtooth', release: 0.1, gain: 0.9 },
  // Siege proc signatures (ADR-0280 P1c). gateBlock is a bright shield clang
  // (square, up high, clear of `bonk`'s soft sine); execute is the collapse —
  // the deepest sweep in the siege set so a 1.5% proc lands with weight;
  // aerialReset RISES (the monster is going home) against every other voice's
  // fall; escortArrive is a two-note horn. All obey the 0.4× envelope clamp.
  gateBlock:    { freq: 980, to: 620, duration: 0.12, type: 'square', release: 0.045, gain: 0.6 },
  // Last Stand (P5): a deep horn that RISES — the one desperate voice that
  // climbs, wide envelope for a once-per-run moment.
  lastStand:    { freq: 130, to: 520, duration: 0.55, type: 'sawtooth', release: 0.2, gain: 0.8 },
  execute:      { freq: 320, to: 90, duration: 0.17, type: 'sawtooth', release: 0.065, gain: 0.75 },
  aerialReset:  { freq: 300, to: 1150, duration: 0.26, type: 'triangle', release: 0.1, gain: 0.5 },
  escortArrive: { freq: 392, to: 523, duration: 0.3, type: 'square', release: 0.11, gain: 0.6 },
  // Grey Hour post office. The cork is the prototype's pressure-release sweep
  // flattened to one exponential leg; the key is a dry, tiny strike.
  corkPop:   { freq: 150, to: 640, duration: 0.13, type: 'triangle', attack: 0.005, release: 0.05, gain: 0.8 },
  typeKey:   { freq: 1750, duration: 0.03, type: 'square', attack: 0.002, release: 0.012, gain: 0.22 },
  // LOOK-room / hollow-planet dive. Soft fall, under corkPop, envelope inside 0.4×.
  paperHush: { freq: 280, to: 110, duration: 0.36, type: 'sine', attack: 0.03, release: 0.12, gain: 0.42 },
  // Naval attacks have a softer fundamental than the tower's square-wave crack,
  // so a rapid broadside leaves room for threats, pickups and system feedback.
  seaCannon:   { freq: 185, to: 62, duration: 0.12, type: 'triangle', attack: 0.004, release: 0.046, gain: 0.48 },
  seaBarrage:  { freq: 240, to: 58, duration: 0.32, type: 'triangle', attack: 0.006, release: 0.12, gain: 0.75 },
  seaPulse:    { freq: 480, to: 95, duration: 0.5, type: 'sine', attack: 0.025, release: 0.19, gain: 0.52 },
  seaRepair:   { freq: 440, to: 880, duration: 0.38, type: 'triangle', attack: 0.02, release: 0.14, gain: 0.38 },
  seaOverdrive:{ freq: 100, to: 420, duration: 0.44, type: 'sawtooth', attack: 0.03, release: 0.16, gain: 0.28 },
  seaLevel:    { freq: 660, to: 1320, duration: 0.34, type: 'triangle', attack: 0.015, release: 0.13, gain: 0.46 },
  seaRefit:    { freq: 520, to: 780, duration: 0.2, type: 'triangle', attack: 0.008, release: 0.075, gain: 0.4 },
  seaSector:   { freq: 260, to: 390, duration: 0.65, type: 'triangle', attack: 0.05, release: 0.24, gain: 0.42 },
  seaVictory:  { freq: 523.25, to: 1046.5, duration: 0.85, type: 'triangle', attack: 0.035, release: 0.32, gain: 0.6 },
};

/**
 * Frequency-multiplier bounds for `PlayOptions.pitch`. Clamped rather than
 * trusted so a caller bug can never hand Web Audio a 0 / negative / NaN
 * frequency (which throws mid-gameplay on Safari).
 */
const PITCH_MIN = 0.25;
const PITCH_MAX = 4;

function clampPitch(pitch: number | undefined): number {
  if (pitch === undefined || !Number.isFinite(pitch)) return 1;
  return Math.max(PITCH_MIN, Math.min(PITCH_MAX, pitch));
}

export interface PlayOptions {
  /**
   * Multiplies the voice's whole frequency curve (start AND ramp target), so
   * the shape of the chirp survives transposition. Used by the siege hit
   * ladder; clamped to [0.25, 4] and ignored when non-finite.
   */
  pitch?: number;
  /**
   * Per-CALL loudness trim on top of the voice's authored `gain` — for
   * repeated micro-cues (the post-office typewriter) that must sit under
   * their own voice at full strength. Clamped to [0, 2]; ignored when
   * non-finite.
   */
  gain?: number;
  /**
   * Where the sound is, left to right: -1 hard left, 0 centre, +1 hard right.
   * Clamped to [-1, 1]; ignored when non-finite or absent, which is every
   * existing caller — a UI click has no position and must not acquire one.
   *
   * Computed by `planet/engine/spatialCue.ts` from the emitter and the camera.
   * Not `PositionalAudio`: that wants an AudioListener on the camera and a
   * buffer per voice, and every voice here is an oscillator built at call
   * time. A StereoPanner is the whole of what it would buy us.
   */
  pan?: number;
}

/**
 * Module-level shared AudioContext. Both the SFX engine and Phase-2 speech
 * playback (`playSpeech` lands in T2) MUST share a single context so timeline
 * math (`getPlaybackTime`) is consistent with what the source nodes hear.
 *
 * Best-effort init: user-gesture requirement means the first call before any
 * interaction may no-op on Safari / Chrome autoplay policy. Returns null in
 * non-browser environments (SSR, vitest's default node env).
 */
let sharedCtx: AudioContext | null = null;
let masterMuted = false;

export function getOrCreateAudioContext(): AudioContext | null {
  if (sharedCtx) return sharedCtx;
  try {
    const Ctor =
      (typeof window !== 'undefined' &&
        ((window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)) ??
      undefined;
    if (!Ctor) return null;
    sharedCtx = new Ctor();
    return sharedCtx;
  } catch {
    return null;
  }
}

/**
 * Internal — clear the module-level shared AudioContext cache so the next
 * `getOrCreateAudioContext` call rebuilds from a fresh `window.AudioContext`
 * lookup. Exists for test isolation only (each speech test stubs its own
 * fake context); production callers should never need this.
 */
export function __resetSharedAudioContext(): void {
  sharedCtx = null;
}

class AudioEngine {
  private volume = 0.5;
  private muted = false;
  private readonly seaVoices = new Map<OscillatorNode, { end: number; close: () => void }>();

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
  }

  setMuted(m: boolean) {
    this.muted = m;
    masterMuted = m;
    if (m) { this.stopSeaVoices(); stopSpeech(); }
  }

  /** Leaving the sea or muting cannot leave an active naval cue connected. */
  stopSeaVoices(): void {
    for (const [osc, voice] of this.seaVoices) {
      try { osc.stop(sharedCtx?.currentTime ?? 0); } catch { /* A naturally ended voice is already quiet. */ }
      voice.close();
    }
  }

  /** Best-effort init (user-gesture requirement means first call may no-op). */
  private ensureCtx(): AudioContext | null {
    return getOrCreateAudioContext();
  }

  play(sfx: Sfx, opts?: PlayOptions) {
    if (this.muted || this.volume <= 0) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    try {
      this.playUnsafe(sfx, opts);
    } catch (err) {
      // Audio should never tank gameplay. Log once per voice at most.
      // (console.warn is allowed by the shared eslint config.)
      console.warn(`[audio] play(${sfx}) failed:`, (err as Error).message);
    }
  }

  private playUnsafe(sfx: Sfx, opts?: PlayOptions) {
    const ctx = this.ensureCtx();
    if (!ctx) return;

    const voice = VOICES[sfx];
    const naval = sfx.startsWith('sea');
    const pitch = clampPitch(opts?.pitch);
    const callGain =
      opts?.gain !== undefined && Number.isFinite(opts.gain)
        ? Math.max(0, Math.min(2, opts.gain))
        : 1;
    const peak = this.volume * 0.35 * (voice.gain ?? 1) * callGain;
    const now = ctx.currentTime;
    if (naval) {
      for (const active of this.seaVoices.values()) if (active.end <= now) active.close();
      // At most eight simultaneous naval cues. A new important signature replaces
      // the oldest tail rather than dropping the player's confirmation sound.
      if (this.seaVoices.size >= 8) {
        const oldest = this.seaVoices.entries().next().value;
        if (oldest) {
          try { oldest[0].stop(now); } catch { /* Already ended. */ }
          oldest[1].close();
        }
      }
    }
    const duration = voice.duration;
    // Clamp attack + release so the sustained segment (attackEnd..releaseStart)
    // never flips negative. Short SFX (e.g. the 40ms click) otherwise throw
    // `Time must be a finite non-negative number` on Safari / older Chromiums.
    const attack = Math.min(voice.attack ?? 0.01, duration * 0.4);
    const release = Math.min(voice.release ?? 0.06, duration * 0.4);
    const sustainStart = now + attack;
    const releaseStart = now + Math.max(attack, duration - release);

    const osc = ctx.createOscillator();
    osc.type = voice.type ?? 'sine';
    osc.frequency.setValueAtTime(voice.freq * pitch, now);
    if (voice.to !== undefined) {
      osc.frequency.linearRampToValueAtTime(voice.to * pitch, now + duration);
    }
    if (voice.detune !== undefined) osc.detune.setValueAtTime(voice.detune, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, sustainStart);
    gain.gain.setValueAtTime(peak, releaseStart);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    // Pan only when asked, and only where the node exists. Safari shipped
    // StereoPannerNode late and the old PannerNode is a different, heavier
    // contract; a missing panner drops to centre rather than throwing, because
    // "you cannot tell which side the letter is on" is a smaller failure than
    // "the cork is silent".
    const pan =
      opts?.pan !== undefined && Number.isFinite(opts.pan)
        ? Math.max(-1, Math.min(1, opts.pan))
        : null;
    let panner: StereoPannerNode | null = null;
    if (pan !== null && typeof ctx.createStereoPanner === 'function') {
      panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(pan, now);
      osc.connect(gain).connect(panner).connect(ctx.destination);
    } else {
      osc.connect(gain).connect(ctx.destination);
    }
    if (naval) {
      let closed = false;
      const close = (): void => {
        if (closed) return;
        closed = true;
        this.seaVoices.delete(osc);
        osc.onended = null;
        for (const node of [osc, gain, panner]) {
          try { node?.disconnect(); } catch { /* Device loss may have detached it already. */ }
        }
      };
      this.seaVoices.set(osc, { end: now + duration + 0.02, close });
      osc.onended = close;
    }
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }
}

export const audio = new AudioEngine();

/** Long-lived nodes for the ambient music bed (held so stop() can tear down). */
interface MusicNodes {
  master: GainNode;
  pad: GainNode;
  osc: OscillatorNode[];
  lfo: OscillatorNode;
  lfoGain: GainNode;
  /** grey-hour preset only: the looping noise surf + its wandering filter. */
  noise?: AudioBufferSourceNode;
  noiseLfo?: OscillatorNode;
}

/** Which bed this engine plays. `drone` is the original three-voice chord;
 *  `grey-hour` is the Grey Hour sea/paper wash (ADR-0264 PR-4): a lower open
 *  fifth + band-passed noise whose filter wanders like slow surf. Procedural
 *  only — rule 090's line holds. */
type MusicPreset = 'drone' | 'grey-hour';

/**
 * Procedural ambient music bed (C-audio). No sampled files — a soft three-voice
 * drone (A2 / E3 / A3) with a slow LFO "breathing" the pad depth, on its own
 * `master` gain through the shared AudioContext. Same accessibility discipline
 * as the SFX engine: honours `prefers-reduced-motion` (no-op), every public
 * method try/caught, clean no-op under SSR / vitest's node env (no AudioContext)
 * so it can never tank gameplay. Opt-in — nothing plays until `start()`.
 */
class MusicEngine {
  private volume = 0.4;
  private muted = false;
  private nodes: MusicNodes | null = null;

  constructor(private preset: MusicPreset = 'drone') {}

  /** True while the music graph is live (a drone is playing). */
  isPlaying(): boolean {
    return this.nodes !== null;
  }

  setMusicVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyMasterGain();
  }

  setMusicMuted(m: boolean) {
    this.muted = m;
    this.applyMasterGain();
  }

  /** Ambient bed sits well below the SFX (0.35 peak) so it never masks cues. */
  private effectiveGain(): number {
    return this.muted ? 0 : this.volume * 0.12;
  }

  private applyMasterGain() {
    if (!this.nodes) return;
    try {
      const ctx = getOrCreateAudioContext();
      if (!ctx) return;
      // setTargetAtTime → a smooth ~0.6s glide, no zipper noise on slider drags.
      this.nodes.master.gain.setTargetAtTime(this.effectiveGain(), ctx.currentTime, 0.2);
    } catch (err) {
      console.warn('[audio] music gain failed:', (err as Error).message);
    }
  }

  start() {
    if (this.nodes) return; // already playing
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    try {
      this.startUnsafe();
    } catch (err) {
      console.warn('[audio] music start failed:', (err as Error).message);
    }
  }

  stop(immediate = false) {
    const nodes = this.nodes;
    this.nodes = null;
    if (!nodes) return;
    try {
      const ctx = getOrCreateAudioContext();
      const now = ctx?.currentTime ?? 0;
      // ~1.2s fade then tear the oscillators down so we don't leak nodes.
      nodes.master.gain.cancelScheduledValues(now);
      if (immediate) {
        nodes.master.gain.setValueAtTime(0, now);
        nodes.master.disconnect();
      } else nodes.master.gain.setTargetAtTime(0, now, 0.4);
      const stopAt = immediate ? now : now + 1.5;
      nodes.osc.forEach((o) => o.stop(stopAt));
      nodes.lfo.stop(stopAt);
      nodes.noise?.stop(stopAt);
      nodes.noiseLfo?.stop(stopAt);
    } catch (err) {
      console.warn('[audio] music stop failed:', (err as Error).message);
    }
  }

  private startUnsafe() {
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(this.effectiveGain(), now + 1.5); // fade-in
    master.connect(ctx.destination);

    // The LFO breathes this inner gain; master stays a clean volume control.
    const pad = ctx.createGain();
    pad.gain.setValueAtTime(0.7, now);
    pad.connect(master);

    // Soft drone. `drone`: A2/E3/A3, the calm open chord. `grey-hour`: a
    // lower, sparser open fifth (D2/A2) — paper-grey, not a melody.
    const freqs = this.preset === 'grey-hour' ? [73.42, 110] : [110, 164.81, 220];
    const osc = freqs.map((f, i) => {
      const o = ctx.createOscillator();
      o.type = i === 2 ? 'triangle' : 'sine';
      o.frequency.setValueAtTime(f, now);
      o.detune.setValueAtTime((i - 1) * 5, now); // gentle spread for warmth
      o.connect(pad);
      o.start(now);
      return o;
    });

    // ~16s breathing cycle on the pad depth (a touch slower for grey-hour).
    const lfo = ctx.createOscillator();
    lfo.frequency.setValueAtTime(this.preset === 'grey-hour' ? 0.045 : 0.06, now);
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(0.25, now);
    lfo.connect(lfoGain).connect(pad.gain);
    lfo.start(now);

    let noise: AudioBufferSourceNode | undefined;
    let noiseLfo: OscillatorNode | undefined;
    if (this.preset === 'grey-hour') {
      // The surf: a 2s looped noise buffer through a narrow band-pass whose
      // centre wanders on its own slow LFO — waves against a paper shore.
      const len = Math.floor(ctx.sampleRate * 2);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;
      const surf = ctx.createBiquadFilter();
      surf.type = 'bandpass';
      surf.frequency.setValueAtTime(320, now);
      surf.Q.setValueAtTime(0.9, now);
      const surfGain = ctx.createGain();
      surfGain.gain.setValueAtTime(0.28, now); // under the pad, over silence
      noise.connect(surf).connect(surfGain).connect(master);
      noiseLfo = ctx.createOscillator();
      noiseLfo.frequency.setValueAtTime(0.08, now); // ~12s wave period
      const noiseLfoGain = ctx.createGain();
      noiseLfoGain.gain.setValueAtTime(140, now); // filter sweep depth (Hz)
      noiseLfo.connect(noiseLfoGain).connect(surf.frequency);
      noiseLfo.start(now);
      noise.start(now);
    }

    this.nodes = { master, pad, osc, lfo, lfoGain, noise, noiseLfo };
  }
}

export const music = new MusicEngine();
/** The Grey Hour sea/paper bed — opt-in like `music`; the julai interior
 *  starts it on entry and stops it on exit (ADR-0264 PR-4). */
export const greyBed = new MusicEngine('grey-hour');

/**
 * Module-level speech start time (Web Audio `ctx.currentTime` units, seconds).
 * Set by `playSpeech` (T2) when starting speech, cleared on stop / natural end.
 * Read by `getPlaybackTime` each animation frame so the viseme driver knows
 * where in the audio we are.
 */
let speechStartTime: number | null = null;

/**
 * Returns the current playback time of the active speech-audio buffer relative
 * to its start, in seconds. Returns 0 when no speech is playing.
 *
 * Implementation: `ctx.currentTime - speechStartTime` when speech is active.
 * Defensively returns 0 if the AudioContext is unavailable (SSR, vitest's
 * default node env, autoplay-blocked Safari before user gesture) so callers
 * never need their own try/catch.
 */
export function getPlaybackTime(): number {
  if (speechStartTime === null) return 0;
  try {
    const ctx = getOrCreateAudioContext();
    if (!ctx) return 0;
    return ctx.currentTime - speechStartTime;
  } catch {
    return 0;
  }
}

/**
 * Internal — called by `playSpeech` (T2) when starting / ending speech
 * playback. Exported so T2 can wire without exposing module-private state and
 * so tests can drive the module through its public surface (no `__internal`
 * shenanigans needed).
 *
 * Pass `null` to clear the start time (speech ended). Pass a number (typically
 * `ctx.currentTime` at speech start) to mark the playback origin.
 */
export function __setSpeechStartTime(t: number | null): void {
  speechStartTime = t;
}

/**
 * Active speech-audio source node, if any. Held at module scope so a second
 * `playSpeech` call can stop the previous one before starting a new buffer.
 */
let currentSource: AudioBufferSourceNode | null = null;
const speechActivityListeners = new Set<(active: boolean) => void>();
function notifySpeechActivity(active: boolean) {
  for (const listener of speechActivityListeners) {
    try { listener(active); } catch { /* Music ownership must not break speech. */ }
  }
}
/** Music priority observes actual speech playback, not the last stored buffer. */
export function subscribeSpeechActivity(listener: (active: boolean) => void): () => void {
  speechActivityListeners.add(listener);
  listener(currentSource !== null);
  return () => { speechActivityListeners.delete(listener); };
}

/**
 * Optional subscriber for natural speech-end events. T6 PersonaReaction wires
 * this to tear down its rAF lip-sync loop when audio finishes on its own
 * (vs. an explicit stopSpeech call). `null` means no subscriber.
 */
let onSpeechEnded: (() => void) | null = null;

/**
 * Play a TTS-decoded speech buffer through the shared AudioContext + stamp
 * the start time so `getPlaybackTime` reports elapsed seconds for the viseme
 * driver. Idempotent w.r.t. an in-progress speech: any previous source is
 * stopped first.
 *
 * The `_timeline` parameter is intentionally unused inside the audio engine —
 * it travels alongside the buffer so the audioStore (and from there the T3
 * visemeDriver / T6 PersonaReaction) can read it. Keeping the signature
 * `(buffer, timeline)` lets callers stash both atomically without a second
 * setter race.
 *
 * Defensive: if the AudioContext can't be created (SSR, vitest's default
 * node env, autoplay-blocked Safari before user gesture), this is a clean
 * no-op so callers don't need their own try/catch.
 */
export function playSpeech(buffer: AudioBuffer, _timeline: VisemeTimeline): void {
  // Replacing a voice keeps the music hold until the replacement owns it.
  // Releasing it between the two sources would briefly restart the MP3.
  clearSpeechSource(false);
  try {
    if (masterMuted || (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)) {
      notifySpeechActivity(false); return;
    }
    const ctx = getOrCreateAudioContext();
    if (!ctx) { notifySpeechActivity(false); return; }
    const source = ctx.createBufferSource();
    currentSource = source;
    source.buffer = buffer;
    source.connect(ctx.destination);
    __setSpeechStartTime(ctx.currentTime);
    source.onended = () => {
      if (currentSource !== source) return;
      __setSpeechStartTime(null); currentSource = null;
      try { source.disconnect(); } catch { /* Already disconnected. */ }
      notifySpeechActivity(false);
      onSpeechEnded?.();
    };
    notifySpeechActivity(true);
    source.start(0);
  } catch {
    clearSpeechSource(false);
    notifySpeechActivity(false);
  }
}

function clearSpeechSource(notify: boolean): void {
  const source = currentSource;
  currentSource = null;
  if (source) {
    source.onended = null;
    try { source.stop(); } catch { /* Already ended or not started. */ }
    try { source.disconnect(); } catch { /* Already disconnected. */ }
  }
  __setSpeechStartTime(null);
  if (source && notify) notifySpeechActivity(false);
}

/** Stop speech without firing the natural-end callback. Safe when idle. */
export function stopSpeech(): void { clearSpeechSource(true); }

/**
 * Subscribe to natural speech-end events. Pass `null` to unsubscribe.
 * Only one subscriber at a time — Phase 2 only has PersonaReaction (T6) using
 * this. If a second consumer ever needs it, switch to a small set of callbacks.
 */
export function setSpeechEndCallback(cb: (() => void) | null): void {
  onSpeechEnded = cb;
}
